import { useState, useEffect, memo, useCallback, useMemo, useRef, RefObject } from 'react';

import { SelectField } from 'UIPackage/MainModule/components/SelectField';
import { TransferElements } from 'UIPackage/MainModule/components/TransferElements';
import { Button } from 'UIPackage/MainModule/components/Button';

import { Breadcrumb, IBreadcrumbItemProps } from './Breadcrumb';
import { SelectOrganisationsItem } from './SelectOrganisationsItem';
import styles from './SelectOrganisations.module.scss';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';

export type OrganisationProps = {
  name?: string;
  fullName: string;
  id: string;
  last?: boolean;
  selectable?: boolean;
  guid?: string;
  children?: OrganisationProps[];
  ervkKnoOrganizationId?: string;
  ervkTerritoryAgencyId?: string;
  ervkTerritoryAgencyVersionId?: string;
  agencyType?: string;
  ervkVersionUuid?: string;
};

interface IInnerNavigationItem {
  id: string;
  name: string;
}

interface ISelectOrganisationsProps {
  disableSelectedId?: string;
  getOrganisations?: (id?: string) => void;
  highlightedSelectedOrganisations?: OrganisationProps[];
  isLoadButtonVisible: boolean;
  isMultiple?: boolean;
  isNavigationRoot?: boolean;
  isNavigationVisible?: boolean;
  isOrganisationsExpanding?: boolean;
  onOrganisationsParentIdChange?: (id?: string) => void;
  onPageLoad: () => void;
  onSelected: (
    organisations: OrganisationProps[] | IDictionarySelectOption<Partial<IKnoOrganizationDictionary>>[],
  ) => void;
  organisations: OrganisationProps[];
  organisationsTitle: string;
  preExpand?: (knoLevel?: string) => void;
  rootNavigation?: string;
  rootStaticNavigation?: string;
  selectedOrganisations: OrganisationProps[];
  selectedOrganisationsTitle: string;
}

export const SelectOrganisations = memo(
  ({
    disableSelectedId,
    getOrganisations,
    highlightedSelectedOrganisations,
    isLoadButtonVisible,
    isMultiple,
    isNavigationRoot,
    isNavigationVisible = true,
    isOrganisationsExpanding = true,
    onOrganisationsParentIdChange,
    onPageLoad,
    onSelected,
    organisations,
    organisationsTitle,
    preExpand,
    rootNavigation = 'НАЧАЛО',
    rootStaticNavigation,
    selectedOrganisations,
    selectedOrganisationsTitle,
  }: ISelectOrganisationsProps) => {
    useEffect(() => {
      setActiveOrganisationsList([]);
    }, [organisations]);
    const [activeOrganisationsList, setActiveOrganisationsList] = useState<Array<OrganisationProps>>([]);
    const [activeSelectedOrganisationsList, setActiveSelectedOrganisationsList] = useState<Array<OrganisationProps>>(
      [],
    );
    const [innerNavigation, setInnerNavigation] = useState<Array<IInnerNavigationItem>>([]);
    const refToScroll = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (isNavigationRoot) {
        setInnerNavigation([]);
      }
    }, [isNavigationRoot]);

    useEffect(() => {
      if (!preExpand) {
        setInnerNavigation([]);
      }
    }, [rootNavigation, preExpand]);

    const makeSelectingElementToggleHandler = useCallback(
      (value: OrganisationProps) => () => {
        setActiveOrganisationsList(prev => {
          const activeItem = prev.find(item => item.id === value.id);

          return activeItem === undefined
            ? [...(isMultiple ? prev : []), value]
            : prev.filter(item => item.id !== activeItem.id);
        });
      },
      [isMultiple],
    );

    const makeSelectedElementToggleHandler = useCallback(
      (value: OrganisationProps) => () => {
        setActiveSelectedOrganisationsList(prev => {
          const activeItem = prev.find(item => item.id === value.id);

          return activeItem === undefined ? [...prev, value] : prev.filter(item => item.id !== activeItem.id);
        });
      },
      [],
    );

    const manyForward = useCallback(() => {
      onSelected([
        ...selectedOrganisations,
        ...organisations.filter(
          item =>
            item.selectable && !selectedOrganisations.some(selectedOrganisation => selectedOrganisation.id === item.id),
        ),
      ]);
      setActiveOrganisationsList([]);
    }, [onSelected, organisations, selectedOrganisations]);

    const oneForward = useCallback(() => {
      if (activeOrganisationsList.length) {
        onSelected([...selectedOrganisations, ...activeOrganisationsList]);
      }
      setActiveSelectedOrganisationsList([...activeSelectedOrganisationsList, ...activeOrganisationsList]);
      setActiveOrganisationsList([]);
    }, [onSelected, selectedOrganisations, activeOrganisationsList, activeSelectedOrganisationsList]);

    const oneBackward = useCallback(() => {
      if (activeSelectedOrganisationsList.length) {
        onSelected(
          selectedOrganisations.filter(
            item =>
              !activeSelectedOrganisationsList.some(
                activeSelectedOrganisation => activeSelectedOrganisation.id === item.id,
              ),
          ),
        );
        setActiveSelectedOrganisationsList([]);
      }
    }, [activeSelectedOrganisationsList, onSelected, selectedOrganisations]);

    const manyBackward = useCallback(() => {
      if (disableSelectedId) {
        onSelected(selectedOrganisations.filter(item => item.id === disableSelectedId));
      } else {
        onSelected([]);
      }
      setActiveSelectedOrganisationsList([]);
    }, [disableSelectedId, onSelected, selectedOrganisations]);

    const onBreadcrumbItemClick = useCallback(
      (index: number, organisationId: string) => () => {
        if (onOrganisationsParentIdChange) {
          onOrganisationsParentIdChange(organisationId);
        }
        if (getOrganisations) {
          getOrganisations(organisationId);
        }
        setInnerNavigation(innerNavigation.slice(0, index + 1));
      },
      [getOrganisations, innerNavigation, onOrganisationsParentIdChange],
    );

    const onBeginClick = useCallback(() => {
      if (onOrganisationsParentIdChange) {
        onOrganisationsParentIdChange();
      }
      if (getOrganisations) {
        getOrganisations();
      }
      setInnerNavigation([]);
    }, [getOrganisations, onOrganisationsParentIdChange]);

    const breadcrumbItems: IBreadcrumbItemProps[] = useMemo(
      () => [
        ...(rootStaticNavigation
          ? [
              {
                name: rootStaticNavigation,
              },
            ]
          : []),
        {
          name: rootNavigation,
          onClick: innerNavigation.length > 0 ? onBeginClick : undefined,
        },
        ...innerNavigation.map((item, index) => {
          const { id, name } = item;
          const isLast = innerNavigation.length === index + 1;

          if (isLast) {
            return { name };
          }

          return {
            name,
            onClick: onBreadcrumbItemClick(index, id),
          };
        }),
      ],
      [innerNavigation, onBeginClick, onBreadcrumbItemClick, rootNavigation, rootStaticNavigation],
    );

    const handleOnExpandClick = useCallback(
      (element: OrganisationProps) => () => {
        const { id, fullName, agencyType } = element;

        if (onOrganisationsParentIdChange) {
          onOrganisationsParentIdChange(id);
        }
        if (getOrganisations) {
          getOrganisations(id);
        }
        if (preExpand) {
          preExpand(agencyType);
        }
        setInnerNavigation &&
          setInnerNavigation(prev => [
            ...prev,
            {
              id,
              name: fullName,
            },
          ]);
      },
      [getOrganisations, onOrganisationsParentIdChange],
    );
    const selectingElementsContent = useMemo(
      () => (
        <>
          {organisations.map(element => (
            <SelectOrganisationsItem
              key={`${element.ervkVersionUuid}-${element.id}-${element.fullName}`}
              name={element.fullName}
              expanding={isOrganisationsExpanding && !element.last}
              active={activeOrganisationsList.indexOf(element) !== -1}
              selectable={element.selectable}
              disabled={selectedOrganisations.some(sameOrganization(element))}
              onToggle={makeSelectingElementToggleHandler(element)}
              onExpand={handleOnExpandClick(element)}
            />
          ))}
          {isLoadButtonVisible && <Button type="button" text="Показать еще" color="loadLink" onClick={onPageLoad} />}
        </>
      ),
      [
        selectedOrganisations,
        activeOrganisationsList,
        handleOnExpandClick,
        makeSelectingElementToggleHandler,
        isOrganisationsExpanding,
        onPageLoad,
        organisations,
        isLoadButtonVisible,
      ],
    );

    const selectedElementsContent = useMemo(
      () =>
        selectedOrganisations.map(element => {
          const activeIndex = activeSelectedOrganisationsList.indexOf(element);

          return (
            <SelectOrganisationsItem
              key={`${element.id}-${element.fullName}`}
              name={element.fullName}
              refToScroll={
                activeIndex === activeSelectedOrganisationsList.length - 1 && activeIndex !== -1
                  ? refToScroll
                  : undefined
              }
              active={activeIndex !== -1}
              selectable={element.id !== disableSelectedId}
              disabled={element.id === disableSelectedId}
              onToggle={makeSelectedElementToggleHandler(element)}
              highlighted={highlightedSelectedOrganisations?.some(sameOrganization(element))}
            />
          );
        }),
      [
        selectedOrganisations,
        highlightedSelectedOrganisations,
        activeSelectedOrganisationsList,
        disableSelectedId,
        makeSelectedElementToggleHandler,
      ],
    );

    const scrollToRef = (ref: RefObject<HTMLDivElement>) => ref.current?.scrollIntoView();

    useEffect(() => {
      scrollToRef(refToScroll);
    }, [onSelected]);

    return (
      <div className={styles.Container}>
        {isNavigationVisible && <Breadcrumb className={styles.Breadcrumb} items={breadcrumbItems} />}
        <div className={styles.Wrapper}>
          <SelectField titleContent={organisationsTitle} elementsContent={selectingElementsContent} />
          <TransferElements
            className={styles.TransferElements}
            manyForward={isMultiple ? manyForward : undefined}
            oneForward={oneForward}
            oneBackward={oneBackward}
            manyBackward={isMultiple ? manyBackward : undefined}
          />
          <SelectField titleContent={selectedOrganisationsTitle} elementsContent={selectedElementsContent} />
        </div>
      </div>
    );
  },
);
const sameOrganization = (o1: OrganisationProps) => (o2: OrganisationProps) => {
  if (o1.id && o1.id === o2.id) return true;

  if (o1.ervkVersionUuid && o1.ervkVersionUuid === o2.ervkVersionUuid) return true;

  return false;
};
